/**
 * useAccessMapNormalization Hook
 * 
 * Custom hook for normalizing accessMap data to ensure usernames are used
 * instead of MongoDB ObjectIds for display purposes.
 * 
 * This hook provides:
 * - Normalization function for accessMaps
 * - Normalization function for entire items (projects, collections, etc.)
 * - Automatic normalization when currentUser changes
 */

import { useCallback, useMemo } from 'react';
import { normalizeAccessMapUsernames } from '../../utils/accessMapUtils';

/**
 * Hook for normalizing accessMap data
 * 
 * @param {Object} currentUser - Current user object with id and username properties
 * @returns {Object} - Object with normalization functions
 *   - normalizeAccessMap: Function to normalize an accessMap
 *   - normalizeItem: Function to normalize an entire item (project/collection)
 *   - normalizeItems: Function to normalize an array of items
 */
export const useAccessMapNormalization = (currentUser) => {
  /**
   * Normalizes a single accessMap
   * @param {Object} accessMap - The accessMap to normalize
   * @returns {Object} - Normalized accessMap with usernames
   */
  const normalizeAccessMap = useCallback((accessMap) => {
    if (!accessMap) return accessMap;
    return normalizeAccessMapUsernames(accessMap, currentUser);
  }, [currentUser]);

  /**
   * Normalizes an entire item (project, collection, etc.)
   * @param {Object} item - The item to normalize
   * @returns {Object} - Item with normalized accessMap
   */
  const normalizeItem = useCallback((item) => {
    if (!item) return item;
    
    if (!item.accessMap) {
      return item;
    }

    return {
      ...item,
      accessMap: normalizeAccessMapUsernames(item.accessMap, currentUser)
    };
  }, [currentUser]);

  /**
   * Normalizes an array of items
   * @param {Array} items - Array of items to normalize
   * @returns {Array} - Array of normalized items
   */
  const normalizeItems = useCallback((items) => {
    if (!Array.isArray(items)) return items;
    
    return items.map(item => normalizeItem(item));
  }, [normalizeItem]);

  return useMemo(() => ({
    normalizeAccessMap,
    normalizeItem,
    normalizeItems
  }), [normalizeAccessMap, normalizeItem, normalizeItems]);
};

